import React, { FC, useState } from 'react';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { AddNewProviderModalForm } from './AddNewProviderModalForm';
import { useTranslation } from 'react-i18next';
import { fetchProviderData, setPaymentGatewayRegister } from '../../store/payments/actions/providers';
import { usePaymentContext } from '../../store/payments/payments-context';
import { ButtonComponent } from '../ButtonComponent';
import { SelectProviderModal } from './SelectProviderModal';
import { useLocation } from 'react-router';
import { useFormik } from 'formik';
import { getProviderFormValidationSchema, providerFormInitialValues } from '../../constants/provider/provider-form';
import { IProviderFiltersProps } from './ProviderFilters';
import { parseSearchParams } from '../../utils/utilities';

export const ProviderSelector: FC = () => {
  const { search } = useLocation();
  const { t } = useTranslation();

  const { paymentContextState, dispatch } = usePaymentContext();
  const { isPaymentGatewayRegisterListLoading, isAddingNewProviderLoading } = paymentContextState;

  const [selectedGateway, setSelectedGateway] = useState<string>('');
  const [isProviderModalOpen, setIsProviderModalOpen] = useState<boolean>(false);
  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  const formik = useFormik({
    initialValues: providerFormInitialValues,
    validationSchema: getProviderFormValidationSchema(t, selectedGateway),
    onSubmit: () => setPaymentGatewayRegister(dispatch, selectedGateway, formik, handleSuccess)

  });
  const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

  const handleCloseProviderModal = (): void => {
    setIsProviderModalOpen(false);
    formik.resetForm();
  };

  const filtersFromUrl: IProviderFiltersProps = parseSearchParams(search);

  const handleSuccess = (): void => {
    handleCloseProviderModal();
    setModalState({
      isOpen: true,
      modalTitle: t('PROVIDER.MODAL_TITLE'),
      content: (
        <div className="fs-6">
          {selectedGateway} {t('PROVIDER.SUCCESS_MODAL_TEXT')}
        </div>
      ),
      confirmButtonText: t('AGENCY.SUCCESS_BTN_NAME'),
      minHeight: 100,
      onClose: closeModal,
      onConfirm: async (): Promise<void> => {
        closeModal();
        await fetchProviderData({ dispatch, searchParams: filtersFromUrl });
      },
    });
  };

  const handleSelectProvider = (provider: string): void => {
    closeModal();
    setSelectedGateway(provider);
    setIsProviderModalOpen(true);
  };

  const openProvidersListModal = (): void => {
    setModalState({
      isOpen: true,
      contentClassName: 'd-block',
      size: 'md',
      content: <SelectProviderModal onSelectProvider={handleSelectProvider} />,
      enableOverflow: false,
    });
  };

  return (
    <div className="mb-5">
      <ButtonComponent
        buttonName="Add"
        additionalClass="btn-sm"
        onButtonClick={openProvidersListModal}
        isDisabled={isPaymentGatewayRegisterListLoading}
      />

      <ModalWindow
        {...modalState}
        onClose={closeModal}
      />

      <ModalWindow
        isOpen={isProviderModalOpen}
        onClose={handleCloseProviderModal}
        modalTitle={`Add ${selectedGateway} payment gateway`}
        backdrop="static"
        isRightSideModal={true}
        contentClassName="d-block h-100"
        size='lg'
        isConfirmLoading={isAddingNewProviderLoading}
        onConfirm={formik.handleSubmit}
        onCancel={handleCloseProviderModal}
        content={<AddNewProviderModalForm
          selectedGateway={selectedGateway}
          formik={formik}
        />}
      />
    </div>
  );
};